import React from 'react';
import styled from 'styled-components';
import { theme as themeConfig } from "../../themes/themes";

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 2rem;
  background-color: #f5f5f5;
  font-family: 'TP Sans', sans-serif;
`;

const ErrorTitle = styled.h1`
  color: ${({ theme }) => theme.text.primary};
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
  max-width: 500px;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.text.primary};
  color: ${({ theme }) => theme.neutral.white};
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.button.primaryHover};
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });

    if (window.ai?.appInsights) {
      window.ai.appInsights.trackEvent({
        name: 'ReactError',
        properties: {
          error: error?.message,
          componentStack: errorInfo?.componentStack,
        },
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            We encountered an unexpected error. Please try refreshing the page or click the button below to reset the application.
          </ErrorMessage>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorMessage style={{ color: themeConfig.status.error, fontSize: '0.875rem' }}>
              {this.state.error.message}
            </ErrorMessage>
          )}
          <RetryButton onClick={this.handleRetry}>
            Try Again
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
